
  import {
    defineComponent,
    ref,
    onMounted,
    computed,
    onBeforeUnmount,
    reactive,
  } from 'vue';
  import { useRouter } from 'vue-router';
  import { setCurrentPageBreadcrumbs } from '@/core/helpers/breadcrumb';
  import { useI18n } from 'vue-i18n/index';
  import { useStore } from 'vuex';
  import { Actions } from '@/store/enums/StoreEnums';
  import { useAbility } from '@casl/vue';
  import Datatable from '@/components/kt-datatable/KTDatatable.vue';
  import Swal from 'sweetalert2/dist/sweetalert2.js';
  import OfficesAdvancedSearch from '@/components/search/OfficesAdvancedSearch.vue';

  export default defineComponent({
    name: 'offices-listing',
    components: {
      Datatable,
      OfficesAdvancedSearch,
    },
    async setup() {
      const { t, te } = useI18n();
      const store = useStore();
      const router = useRouter();
      const { can } = useAbility();

      const translate = (text) => {
        if (te(text)) {
          return t(text);
        } else {
          return text;
        }
      };

      const loading = ref(false);

      // Local summary state - updated from API response
      const summary = reactive({
        brokersCount: 0,
        marketeersCount: 0,
        totalCount: 0,
        filteredCount: 0,
      });

      // Helper to update summary from API response
      const updateSummaryFromResponse = (response) => {
        if (response?.summary) {
          summary.brokersCount = response.summary.brokersCount || 0;
          summary.marketeersCount = response.summary.marketeersCount || 0;
          summary.totalCount = response.summary.totalCount || 0;
          summary.filteredCount = response.summary.filteredCount || 0;
        }
      };

      onMounted(() => {
        setCurrentPageBreadcrumbs(translate('officesListing'), [
          translate('members'),
        ]);
      });

      onBeforeUnmount(() => {
        store.dispatch(Actions.RESET_USERS_STORE);
      });

      // Initial load
      await store.dispatch(Actions.GET_OFFICES).then(updateSummaryFromResponse);

      const tableHeader = ref([
        {
          name: translate('status'),
          key: 'is_active',
          sortable: false,
        },
        {
          name: translate('actions'),
          key: 'actions',
        },
        {
          name: translate('id'),
          key: 'id',
          sortable: true,
        },
        {
          name: translate('name'),
          key: 'name',
          sortable: true,
        },
        {
          name: translate('type'),
          key: 'memberTypeLabel',
          sortable: true,
        },
        {
          name: translate('phoneNumber'),
          key: 'phone',
          sortable: true,
        },
        {
          name: translate('emailAddress'),
          key: 'email',
          sortable: true,
        },
        {
          name: translate('lastAd'),
          key: 'lastAdDate',
          sortable: true,
        },
        {
          name: translate('totalAds'),
          key: 'totalAds',
          sortable: true,
        },
        {
          name: translate('remainingAds'),
          key: 'remainingAds',
          sortable: true,
        },
        {
          name: translate('createdDate'),
          key: 'createdAt',
          sortable: true,
        },
        {
          name: translate('badgeIcon'),
          key: 'badgeIcon',
          sortable: true,
        },
        {
          name: translate('hisbaPartner'),
          key: 'verificationIcon',
          sortable: false,
        },
      ]);

      const formatDate = (dateString: string) => {
        if (!dateString) return '';
        const date = new Date(dateString);
        return date.toLocaleString('en-US', {
          year: 'numeric',
          month: '2-digit',
          day: '2-digit',
          hour: '2-digit',
          minute: '2-digit',
          hour12: false,
        });
      };

      const getMemberTypeBadgeClass = (memberType: string) => {
        switch (memberType) {
          case 'broker':
            return 'badge-light-primary';
          case 'marketeer':
            return 'badge-light-success';
          default:
            return 'badge-light-secondary';
        }
      };

      const viewOfficeOverview = async (id) => {
        router.push({ name: 'offices-overview', params: { id } });
        await store.dispatch(Actions.ADD_BODY_CLASSNAME, 'page-loading-over');
      };

      const updateOffice = async (id) => {
        router.push({ name: 'offices-editing', params: { id } });
        await store.dispatch(Actions.ADD_BODY_CLASSNAME, 'page-loading-over');
      };

      const toggleStatusUser = async (user) => {
        const deletedMessage = user.isActive
          ? 'confirmInActive'
          : 'confirmActive';
        const result = await Swal.fire({
          title: translate('areYouSureQ'),
          icon: 'warning',
          text: translate(deletedMessage),
          showCancelButton: true,
          buttonsStyling: false,
          confirmButtonText: translate('confirm'),
          cancelButtonText: translate('cancel'),
          customClass: {
            confirmButton: 'btn fw-bold btn-warning',
            cancelButton: 'btn btn-light',
          },
        });

        // const toggledVerificationStatus = async (user) => {
        //   const deletedMessage = user.isVerifiedOffice
        //     ? 'confirmInActive'
        //     : 'confirmActive';
        //   const result = await Swal.fire({
        //     title: translate('areYouSureQ'),
        //     icon: 'warning',
        //     text: translate(deletedMessage),
        //     showCancelButton: true,
        //     buttonsStyling: false,
        //     confirmButtonText: translate('confirm'),
        //     cancelButtonText: translate('cancel'),
        //     customClass: {
        //       confirmButton: 'btn fw-bold btn-warning',
        //       cancelButton: 'btn btn-light',
        //     },
        //   });

        //   if (result.value) {
        //     loading.value = true;
        //     await store.dispatch(Actions.UPDATE_VERIFICATION, user.id);
        //     await store.dispatch(Actions.GET_OFFICES);
        //     loading.value = false;
        //     const [errorName] = Object.keys(store.getters.getErrors);
        //     const error = store.getters.getErrors[errorName];
        //     if (error) {
        //       Swal.fire({
        //         text: translate(error[0]),
        //         icon: 'error',
        //         buttonsStyling: false,
        //         confirmButtonText: translate('tryAgainExcl'),
        //         customClass: {
        //           confirmButton: 'btn fw-bold btn-light-danger',
        //         },
        //       });
        //     } else {
        //       const successMsg = user.isActive
        //         ? 'SUCCESSFULLY_INACTIVATED'
        //         : 'SUCCESSFULLY_ACTIVEATED';
        //       Swal.fire({
        //         text: translate(successMsg),
        //         icon: 'success',
        //         buttonsStyling: false,
        //         confirmButtonText: translate('ok'),
        //         customClass: {
        //           confirmButton: 'btn btn-light-primary',
        //         },
        //       });
        //     }
        //   }
        // };

        if (result.value) {
          loading.value = true;
          await store.dispatch(Actions.UPDATE_USER, user.id);
          await store
            .dispatch(Actions.GET_OFFICES)
            .then(updateSummaryFromResponse);
          loading.value = false;
          const [errorName] = Object.keys(store.getters.getErrors);
          const error = store.getters.getErrors[errorName];
          if (error) {
            Swal.fire({
              text: translate(error[0]),
              icon: 'error',
              buttonsStyling: false,
              confirmButtonText: translate('tryAgainExcl'),
              customClass: {
                confirmButton: 'btn fw-bold btn-light-danger',
              },
            });
          } else {
            const successMsg = user.isActive
              ? 'SUCCESSFULLY_INACTIVATED'
              : 'SUCCESSFULLY_ACTIVEATED';
            Swal.fire({
              text: translate(successMsg),
              icon: 'success',
              buttonsStyling: false,
              confirmButtonText: translate('ok'),
              customClass: {
                confirmButton: 'btn btn-light-primary',
              },
            });
          }
        }
      };

      const goTo = (pageName) => {
        router.push({ name: pageName });
      };

      const pageChanged = async (val) => {
        loading.value = true;
        await store.dispatch(Actions.UPDATE_USER_CURRENT_PAGE, val);
        await store
          .dispatch(Actions.GET_OFFICES)
          .then(updateSummaryFromResponse);
        loading.value = false;
      };

      const onFilterChange = async () => {
        loading.value = true;
        await store.dispatch(Actions.UPDATE_USER_CURRENT_PAGE, 1);
        await store
          .dispatch(Actions.GET_OFFICES)
          .then(updateSummaryFromResponse);
        loading.value = false;
      };

      const onResetFilters = async () => {
        loading.value = true;
        await store.dispatch(Actions.UPDATE_USER_CURRENT_PAGE, 1);
        await store
          .dispatch(Actions.GET_OFFICES)
          .then(updateSummaryFromResponse);
        loading.value = false;
      };

      store.dispatch(Actions.REMOVE_BODY_CLASSNAME, 'page-loading-over');

      return {
        tableData: computed(() => store.getters.usersList),
        tableHeader,
        toggleStatusUser,
        translate,
        totalItems: computed(() => store.getters.listUsersCount),
        pageChanged,
        viewOfficeOverview,
        goTo,
        loading,
        can,
        updateOffice,
        onFilterChange,
        onResetFilters,
        summary,
        formatDate,
        getMemberTypeBadgeClass,
      };
    },
  });
